import * as React from 'react'
import { useContext, useMemo } from 'react'
import { CompoundButton } from '@fluentui/react-components'
import * as UUID from 'uuid'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { useFormikContext } from 'formik'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import { LegalAidCertificateInvoiceProps } from './Invoices'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import useComposeInvoiceOutlookMessage from '../../../hooks/finance/invoice/useComposeInvoiceOutlookMessage'
import usePersistLegalAidCertificateInvoice from '../../../hooks/finance/invoice/usePersistLegalAidCertificateInvoice'
import { bundleIcon, DocumentQueueAddFilled, DocumentQueueAddRegular } from '@fluentui/react-icons'
import { QueueStatus } from '../../../hooks/queue/queueTypes'
import useSubmitInvoiceDocumentGeneration from '../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import { DocumentGenerationType } from '../../../hooks/document/documentGenerationTypes'
import { PersistInvoiceDto } from '../../../hooks/finance/invoice/usePersistInvoice'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import { MainDto } from '../../../config/filePartyConfig'

const DocumentQueueIcon = bundleIcon(DocumentQueueAddFilled, DocumentQueueAddRegular)

export default function CreateContributionInvoiceButton(
    props: LegalAidCertificateInvoiceProps & { canCreateInvoice: boolean; pending: boolean; setPending: (pending: boolean) => void },
) {
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { values, setFieldValue, setFieldError } = useFormikContext<LegalAidCertificateDto>()
    const { t: translate } = useTranslation()
    const axios = useAxios()
    const requestId = useMemo(() => UUID.v4(), [props.selected?.id, values.contributionInvoiceId])
    const persist = usePersistLegalAidCertificateInvoice(props.file, props.parties)
    const generatePdf = useSubmitInvoiceDocumentGeneration(DocumentGenerationType.Invoice)
    const composeOutlookMessage = useComposeInvoiceOutlookMessage()

    const hasContributionAmount = useMemo<boolean>(() => Number(values.contribution) > 0, [values.contribution])
    const canCreateContributionInvoice = useMemo<boolean>(
        () => !values.contributionInvoiceId && props.canCreateInvoice && hasContributionAmount && !props.pending,
        [props.canCreateInvoice, props.pending, values.contributionInvoiceId, hasContributionAmount],
    )
    const createContributionInvoice = async () => {
        props.setPending(true)
        try {
            // Persist the certificate (and any new legal aid board party) first: an unsaved form has no
            // stored certificate to link the invoice to.
            const saved = await props.saveCertificate(values, setFieldError)
            if (!saved) {
                props.setPending(false)
                return
            }
            // Select the stored certificate and refresh the lists only once the flow is done: the refresh
            // remounts the Formik subtree.
            const finish = () => {
                props.setSelected({ ...props.selected, id: saved.certificateId, isNew: false, isDirty: false })
                props.reset?.()
                props.setPending(false)
            }
            // Fetch the parties again, as the debtor may not be in the loaded list yet.
            const parties = await axios.get<MainDto[]>(`/file/${values.fileId}/party`).then(response => response.data)
            const request = persist(
                requestId,
                {
                    templateId: settings.contributionInvoice.templateId,
                    clientPivotId: values.clientPivotId,
                    debtorPivotId: values.clientPivotId,
                    debtor: parties.find(party => party.pivotId === values.clientPivotId),
                    currency: values.currency,
                    recipientReference: values.reference,
                },
                (data: PersistInvoiceDto) => {
                    // Link the invoice to the certificate as soon as it exists, independent of the PDF
                    // generation, through an explicit save: submitting the Formik state right after a
                    // setFieldValue races the batched state update and can persist stale values.
                    props
                        .saveCertificate(
                            { ...values, legalAidBoardPivotId: saved.legalAidBoardPivotId, contributionInvoiceId: data.invoiceId },
                            setFieldError,
                            { certificateId: saved.certificateId },
                        )
                        .then(() => setFieldValue('contributionInvoiceId', data.invoiceId))
                    generatePdf(data, {
                        [QueueStatus.Finished]: pdfData => {
                            if (isAppContext([AppContext.Read, AppContext.Module])) {
                                composeOutlookMessage(
                                    data.invoiceId,
                                    parties.find(party => party.pivotId === values.clientPivotId)?.email,
                                    pdfData.status.driveItem,
                                )
                            }
                            finish()
                        },
                        [QueueStatus.Cancelled]: finish,
                        [QueueStatus.Failed]: finish,
                    })
                },
                { [settings.contributionInvoice.feeTemplateId]: values.contribution },
            )
            if (request) {
                request.catch(finish)
            } else {
                finish()
            }
        } catch {
            props.setPending(false)
        }
    }

    return (
        <CompoundButton
            disabled={!canCreateContributionInvoice}
            secondaryContent={
                hasContributionAmount || values.contributionInvoiceId
                    ? 'Verstuur declaratie eigen bijdrage'
                    : translate('legalaidcertificate.contribution.amount.required')
            }
            appearance="subtle"
            icon={<DocumentQueueIcon />}
            onClick={createContributionInvoice}
        >
            Eigen bijdrage
        </CompoundButton>
    )
}
